import React, { useState, useEffect, useCallback } from 'react';
import {
    ClipboardDocumentListIcon,
    CheckCircleIcon,
    ClockIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import LayoutWrapper from '../components/LayoutWrapper';
import Helper from '../helpers';
import config from '../config';
import '../styles/markdown.css';

const baseUrl = config.apiBaseUrl;

const MyPlans = () => {
    const [currentVenueKey, setCurrentVenueKey] = useState("");
    const [userId, setUserId] = useState("");
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedPlan, setExpandedPlan] = useState(null);
    const [exercises, setExercises] = useState([]);
    const [progress, setProgress] = useState([]);

    // Workout logging modal
    const [showLogModal, setShowLogModal] = useState(false);
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
    const [logForm, setLogForm] = useState({
        actualSets: '',
        actualReps: '',
        actualDurationSeconds: '',
        notes: ''
    });

    const fetchUserPlans = useCallback(async () => {
        if (!currentVenueKey || !userId) return;

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/users/${userId}/plans`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setPlans(data);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setLoading(false);
        }
    }, [currentVenueKey, userId]);

    const fetchPlanDetails = async (assignmentId) => {
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/assignments/${assignmentId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();

                // Fetch exercises for this plan's template
                const exercisesResponse = await fetch(
                    `${baseUrl}/api/venues/${currentVenueKey}/plans/templates/${data.templateId}/exercises`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        credentials: 'include'
                    }
                );

                if (exercisesResponse.ok) {
                    const exercisesData = await exercisesResponse.json();
                    setExercises(exercisesData);
                }

                // Fetch progress
                const progressResponse = await fetch(
                    `${baseUrl}/api/venues/${currentVenueKey}/plans/assignments/${assignmentId}/progress`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                        credentials: 'include'
                    }
                );

                if (progressResponse.ok) {
                    const progressData = await progressResponse.json();
                    setProgress(progressData);
                }
            }
        } catch (error) {
            console.error('Error fetching plan details:', error);
        }
    };

    const togglePlanExpansion = (planId) => {
        if (expandedPlan === planId) {
            setExpandedPlan(null);
            setExercises([]);
            setProgress([]);
        } else {
            setExpandedPlan(planId);
            fetchPlanDetails(planId);
        }
    };

    const handleLogProgress = async (exercise, assignmentId) => {
        setSelectedExercise(exercise);
        setSelectedAssignmentId(assignmentId);
        setLogForm({
            actualSets: exercise.sets || '',
            actualReps: exercise.reps || '',
            actualDurationSeconds: exercise.durationSeconds || '',
            notes: ''
        });
        setShowLogModal(true);
    };

    const submitWorkoutLog = async (e) => {
        e.preventDefault();

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(
                `${baseUrl}/api/venues/${currentVenueKey}/plans/assignments/${selectedAssignmentId}/progress`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        exerciseId: selectedExercise.id,
                        actualSets: logForm.actualSets ? parseInt(logForm.actualSets) : null,
                        actualReps: logForm.actualReps ? parseInt(logForm.actualReps) : null,
                        actualDurationSeconds: logForm.actualDurationSeconds ? parseInt(logForm.actualDurationSeconds) : null,
                        notes: logForm.notes
                    })
                }
            );

            if (response.ok) {
                const result = await response.json();
                
                // Check if this was a duplicate logging (already logged today)
                if (result.alreadyLoggedToday) {
                    alert('⚠️ You have already logged this exercise today!\n\nYou won\'t receive any additional points for logging it again. Points are only awarded for the first time you complete each exercise per day.');
                }
                
                // Refresh progress and plans to show updated stats
                fetchPlanDetails(selectedAssignmentId);
                fetchUserPlans();
                setShowLogModal(false);
                setLogForm({ actualSets: '', actualReps: '', actualDurationSeconds: '', notes: '' });
            }
        } catch (error) {
            console.error('Error logging progress:', error);
        }
    };

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);
            const venueKey = await Helper.getUserVenueId();
            setCurrentVenueKey(venueKey);

            // Get user ID from JWT claims
            const userIdFromToken = await Helper.getUserId();
            setUserId(userIdFromToken);
        };
        initialize();
    }, []);

    useEffect(() => {
        if (currentVenueKey && userId) {
            fetchUserPlans();
        }
    }, [currentVenueKey, userId, fetchUserPlans]);

    const groupExercisesByDay = (exercises, plan) => {
        const grouped = {};
        
        // Parse day mapping if it exists (now maps exercise IDs to weekdays)
        const dayMapping = plan?.dayMapping ? JSON.parse(plan.dayMapping) : null;
        const scheduleType = plan?.scheduleType || 'sequential';

        if (scheduleType === 'specific_days' && dayMapping) {
            // Group exercises by their assigned weekday
            exercises.forEach(exercise => {
                const assignedDay = dayMapping[exercise.id];
                const dayLabel = assignedDay || 'Anytime';
                
                if (!grouped[dayLabel]) {
                    grouped[dayLabel] = {
                        dayLabel: dayLabel,
                        exercises: []
                    };
                }
                grouped[dayLabel].exercises.push(exercise);
            });

            // Sort by weekday order (or put "Anytime" last)
            const weekdayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Anytime'];
            return Object.values(grouped).sort((a, b) => {
                return weekdayOrder.indexOf(a.dayLabel) - weekdayOrder.indexOf(b.dayLabel);
            });
        } else {
            // Ad hoc mode - all exercises in one group
            return [{
                dayLabel: 'Exercises (Complete Anytime)',
                exercises: exercises
            }];
        }
    };

    if (loading) {
        return (
            <LayoutWrapper title="My Workout Plans">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin-luxury rounded-full h-12 w-12 border-4 border-emerald-500"></div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper
            title="My Workout Plans"
            subtitle="View and track your personalized training plans"
        >
            {plans.length === 0 ? (
                <div className="text-center py-12 card-luxury">
                    <ClipboardDocumentListIcon className="h-16 w-16 text-emerald-600/50 mx-auto mb-4" />
                    <h3 className="text-xl font-display font-semibold text-accent-platinum mb-2">No Plans Assigned</h3>
                    <p className="text-emerald-600/60 font-body">Your trainer hasn't assigned any workout plans yet.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {plans.map(plan => (
                        <div key={plan.id} className="card-luxury hover-lift overflow-hidden">
                            {/* Plan Header */}
                            <div
                                className="p-6 cursor-pointer hover:bg-luxury-slate/30 transition-all duration-300"
                                onClick={() => togglePlanExpansion(plan.id)}
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                            <h3 className="text-2xl font-display font-bold text-gradient-emerald">{plan.templateName}</h3>
                                            <span className={`badge-luxury ${
                                                plan.status === 'active' ? 'bg-performance-neon/20 text-performance-neon border-performance-neon/30' :
                                                plan.status === 'completed' ? 'bg-performance-cyan/20 text-performance-cyan border-performance-cyan/30' :
                                                'bg-emerald-600/10 text-emerald-400 border-emerald-600/20'
                                            }`}>
                                                {plan.status}
                                            </span>
                                            {plan.templateCompletionBonusPoints > 0 && (
                                                <span className="badge-luxury bg-emerald-gradient border-emerald-500/50 text-luxury-black font-bold">
                                                    🎁 +{plan.templateCompletionBonusPoints} bonus pts!
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-accent-platinum/80 text-sm mb-3 font-body">{plan.templateDescription}</p>
                                        <div className="flex items-center gap-4 text-xs text-emerald-600/60 font-body">
                                            <span className="flex items-center gap-1">
                                                <ClockIcon className="h-4 w-4" />
                                                Assigned: {new Date(plan.assignedDate).toLocaleDateString()}
                                            </span>
                                            {plan.assignedBy && (
                                                <span>By: {plan.assignedBy}</span>
                                            )}
                                        </div>
                                        {plan.customizationNotes && (
                                            <div className="mt-3 p-3 glass-luxury rounded-lg">
                                                <p className="text-sm text-performance-cyan font-body">
                                                    <strong className="font-display">Notes from your trainer:</strong> {plan.customizationNotes}
                                                </p>
                                            </div>
                                        )}
                                        
                                        {/* Gamification Stats */}
                                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <div className="glass-luxury px-4 py-3 rounded-xl text-center">
                                                <p className="text-xs text-emerald-600/60 font-display tracking-wide uppercase mb-1">Points</p>
                                                <p className="text-xl font-display font-bold text-gradient-emerald">🏆 {plan.totalPoints}</p>
                                            </div>
                                            <div className="glass-luxury px-4 py-3 rounded-xl text-center">
                                                <p className="text-xs text-emerald-600/60 font-display tracking-wide uppercase mb-1">Current Streak</p>
                                                <p className="text-xl font-display font-bold text-performance-energy">🔥 {plan.currentStreak}</p>
                                            </div>
                                            <div className="glass-luxury px-4 py-3 rounded-xl text-center">
                                                <p className="text-xs text-emerald-600/60 font-display tracking-wide uppercase mb-1">Best Streak</p>
                                                <p className="text-xl font-display font-bold text-accent-platinum">⭐ {plan.longestStreak}</p>
                                            </div>
                                            <div className="glass-luxury px-4 py-3 rounded-xl text-center">
                                                <p className="text-xs text-emerald-600/60 font-display tracking-wide uppercase mb-1">Progress</p>
                                                <p className="text-xl font-display font-bold text-performance-cyan">
                                                    {plan.completedExercises}/{plan.totalExercises}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <button className="ml-4 text-emerald-600/60 hover:text-emerald-300 transition-colors">
                                        {expandedPlan === plan.id ? (
                                            <ChevronUpIcon className="h-6 w-6" />
                                        ) : (
                                            <ChevronDownIcon className="h-6 w-6" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Expanded Plan Details */}
                            {expandedPlan === plan.id && (
                                <div className="border-t border-emerald-600/10 p-6 bg-luxury-obsidian/50">
                                    {/* Markdown Content Sections */}
                                    {(plan.templateGoals || plan.templateNotes || plan.templateInstructions) && (
                                        <div className="mb-6 space-y-4">
                                            {/* Goals Section */}
                                            {plan.templateGoals && (
                                                <div className="glass-luxury border border-performance-neon/30 rounded-xl p-5">
                                                    <h5 className="text-performance-neon font-display font-semibold mb-3 flex items-center gap-2 text-lg">
                                                        <span>🎯</span> Goals
                                                    </h5>
                                                    <div className="prose prose-invert prose-sm max-w-none text-accent-platinum/90 font-body">
                                                        <ReactMarkdown>{plan.templateGoals}</ReactMarkdown>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Instructions Section */}
                                            {plan.templateInstructions && (
                                                <div className="glass-luxury border border-performance-cyan/30 rounded-xl p-5">
                                                    <h5 className="text-performance-cyan font-display font-semibold mb-3 flex items-center gap-2 text-lg">
                                                        <span>📋</span> Instructions
                                                    </h5>
                                                    <div className="prose prose-invert prose-sm max-w-none text-accent-platinum/90 font-body">
                                                        <ReactMarkdown>{plan.templateInstructions}</ReactMarkdown>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Notes Section */}
                                            {plan.templateNotes && (
                                                <div className="glass-luxury border border-emerald-500/30 rounded-xl p-5">
                                                    <h5 className="text-emerald-300 font-display font-semibold mb-3 flex items-center gap-2 text-lg">
                                                        <span>📝</span> Notes
                                                    </h5>
                                                    <div className="prose prose-invert prose-sm max-w-none text-accent-platinum/90 font-body">
                                                        <ReactMarkdown>{plan.templateNotes}</ReactMarkdown>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="divider-luxury my-6" />
                                    <h4 className="text-xl font-display font-bold text-gradient-emerald mb-6">Weekly Schedule</h4>

                                    {exercises.length === 0 ? (
                                        <p className="text-emerald-600/60 text-center py-8 font-body">Loading exercises...</p>
                                    ) : (
                                        <div className="space-y-6">
                                            {groupExercisesByDay(exercises, plan).map((day, index) => (
                                                <div key={index} className="glass-luxury rounded-xl p-5 border border-emerald-600/10">
                                                    <h5 className="text-emerald-300 font-display font-semibold mb-4 text-lg tracking-wide">{day.dayLabel}</h5>
                                                    <div className="space-y-3">
                                                        {day.exercises
                                                            .sort((a, b) => a.orderIndex - b.orderIndex)
                                                            .map((exercise) => {
                                                                const isCompleted = progress.some(
                                                                    p => p.exerciseId === exercise.id
                                                                );

                                                                return (
                                                                    <div
                                                                        key={exercise.id}
                                                                        className="flex items-start gap-4 p-4 bg-luxury-charcoal/50 rounded-xl border border-emerald-600/10 hover:border-emerald-500/30 transition-all duration-300"
                                                                    >
                                                                        <div className="flex-shrink-0 mt-1">
                                                                            {isCompleted ? (
                                                                                <CheckCircleIcon className="h-6 w-6 text-performance-neon drop-shadow-lg" />
                                                                            ) : (
                                                                                <div className="h-6 w-6 rounded-full border-2 border-emerald-600/40" />
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-1">
                                                                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                                                <h6 className="text-accent-platinum font-display font-semibold text-base">{exercise.name}</h6>
                                                                                {isCompleted && (
                                                                                    <span className="badge-luxury bg-emerald-600/20 text-emerald-300 border-emerald-500/40 text-xs">
                                                                                        ✓ Logged today
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            {exercise.description && (
                                                                                <p className="text-accent-platinum/70 text-sm mb-3 font-body">{exercise.description}</p>
                                                                            )}
                                                                            {isCompleted && (
                                                                                <div className="mb-3 p-3 glass-luxury border border-emerald-500/20 rounded-lg text-xs text-emerald-300 font-body">
                                                                                    💡 You've already logged this exercise today. You can log it again but won't earn extra points.
                                                                                </div>
                                                                            )}
                                                                            <div className="flex flex-wrap gap-3 text-xs text-emerald-600/70 font-body">
                                                                                {exercise.sets && (
                                                                                    <span className="px-2 py-1 bg-luxury-obsidian/50 rounded">Sets: {exercise.sets}</span>
                                                                                )}
                                                                                {exercise.reps && (
                                                                                    <span className="px-2 py-1 bg-luxury-obsidian/50 rounded">Reps: {exercise.reps}</span>
                                                                                )}
                                                                                {exercise.durationSeconds && (
                                                                                    <span className="px-2 py-1 bg-luxury-obsidian/50 rounded">Duration: {exercise.durationSeconds >= 60 ? `${Math.floor(exercise.durationSeconds / 60)}min ${exercise.durationSeconds % 60 > 0 ? `${exercise.durationSeconds % 60}s` : ''}` : `${exercise.durationSeconds}s`}</span>
                                                                                )}
                                                                                {exercise.restSeconds && (
                                                                                    <span className="px-2 py-1 bg-luxury-obsidian/50 rounded">Rest: {exercise.restSeconds}s</span>
                                                                                )}
                                                                                {exercise.videoUrl && (
                                                                                    <a href={exercise.videoUrl} target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-performance-cyan/10 text-performance-cyan hover:text-performance-electric rounded transition-colors">📹 Video</a>
                                                                                )}
                                                                            </div>
                                                                            {exercise.notes && (
                                                                                <p className="text-xs text-emerald-600/50 mt-2 italic font-body">{exercise.notes}</p>
                                                                            )}
                                                                        </div>
                                                                        <button
                                                                            onClick={() => handleLogProgress(exercise, plan.id)}
                                                                            className="btn-performance text-xs px-4 py-2 whitespace-nowrap font-semibold"
                                                                        >
                                                                            Log
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Progress Summary */}
                                    {progress.length > 0 && (
                                        <div className="mt-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                                            <h5 className="text-emerald-400 font-semibold mb-2">Progress</h5>
                                            <p className="text-sm text-gray-300">
                                                Completed {progress.length} workout{progress.length !== 1 ? 's' : ''}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Workout Logging Modal */}
            {showLogModal && selectedExercise && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="card-luxury p-8 max-w-md w-full animate-slide-up">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-display font-bold text-gradient-emerald">Log Workout</h3>
                            <button onClick={() => setShowLogModal(false)} className="text-emerald-600/60 hover:text-emerald-300 transition-colors">
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="mb-6 p-4 glass-luxury rounded-xl border border-emerald-600/20">
                            <h4 className="text-accent-platinum font-display font-semibold">{selectedExercise.name}</h4>
                            <p className="text-sm text-accent-platinum/70 mt-1 font-body">{selectedExercise.description}</p>
                            <div className="flex gap-3 mt-3 text-xs text-emerald-400 font-body">
                                {selectedExercise.sets && <span className="px-2 py-1 bg-emerald-600/10 rounded">Target: {selectedExercise.sets} sets</span>}
                                {selectedExercise.reps && <span className="px-2 py-1 bg-emerald-600/10 rounded">× {selectedExercise.reps} reps</span>}
                                {selectedExercise.durationSeconds && <span className="px-2 py-1 bg-emerald-600/10 rounded">{selectedExercise.durationSeconds >= 60 ? `${Math.floor(selectedExercise.durationSeconds / 60)}min ${selectedExercise.durationSeconds % 60 > 0 ? `${selectedExercise.durationSeconds % 60}s` : ''}` : `${selectedExercise.durationSeconds}s`}</span>}
                            </div>
                            <p className="text-xs text-emerald-300 mt-3 font-display font-semibold">🏆 Worth {selectedExercise.pointsValue || 10} points!</p>
                        </div>

                        <form onSubmit={submitWorkoutLog}>
                            <div className="space-y-4">
                                {selectedExercise.sets && (
                                    <div>
                                        <label className="block text-sm font-display font-medium text-emerald-300/80 mb-2">
                                            Sets Completed
                                        </label>
                                        <input
                                            type="number"
                                            value={logForm.actualSets}
                                            onChange={(e) => setLogForm({...logForm, actualSets: e.target.value})}
                                            className="input-field"
                                            min="0"
                                        />
                                    </div>
                                )}
                                {selectedExercise.reps && (
                                    <div>
                                        <label className="block text-sm font-display font-medium text-emerald-300/80 mb-2">
                                            Reps per Set
                                        </label>
                                        <input
                                            type="number"
                                            value={logForm.actualReps}
                                            onChange={(e) => setLogForm({...logForm, actualReps: e.target.value})}
                                            className="input-field"
                                            min="0"
                                        />
                                    </div>
                                )}
                                {selectedExercise.durationSeconds && (
                                    <div>
                                        <label className="block text-sm font-display font-medium text-emerald-300/80 mb-2">
                                            Duration (seconds)
                                        </label>
                                        <input
                                            type="number"
                                            value={logForm.actualDurationSeconds}
                                            onChange={(e) => setLogForm({...logForm, actualDurationSeconds: e.target.value})}
                                            className="input-field"
                                            min="0"
                                            placeholder="e.g., 60 for 1 min"
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-display font-medium text-emerald-300/80 mb-2">
                                        Notes (optional)
                                    </label>
                                    <textarea
                                        value={logForm.notes}
                                        onChange={(e) => setLogForm({...logForm, notes: e.target.value})}
                                        className="input-field font-body"
                                        rows="3"
                                        placeholder="How did it feel? Any observations?"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-3 mt-8">
                                <button
                                    type="button"
                                    onClick={() => setShowLogModal(false)}
                                    className="btn-outline flex-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary flex-1"
                                >
                                    💪 Log Workout
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </LayoutWrapper>
    );
};

export default MyPlans;
